import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('tuprima_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Raffles
  getActiveRaffle: async () => {
    const response = await axios.get(`${API_BASE}/raffles/active`);
    return response.data;
  },

  getRaffleHistory: async () => {
    const response = await axios.get(`${API_BASE}/raffles/history`);
    return response.data;
  },

  getRaffle: async (raffleId) => {
    const response = await axios.get(`${API_BASE}/raffles/${raffleId}`);
    return response.data;
  },

  // Packages
  getPackages: async () => {
    const response = await axios.get(`${API_BASE}/packages`);
    return response.data;
  },

  // Tickets
  buyTickets: async (raffleId, packageTickets) => {
    const response = await axios.post(
      `${API_BASE}/tickets/buy`,
      { raffle_id: raffleId, package_tickets: packageTickets },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  getMyTickets: async (raffleId = null) => {
    const params = raffleId ? `?raffle_id=${raffleId}` : '';
    const response = await axios.get(`${API_BASE}/tickets/my-tickets${params}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getMyPurchases: async () => {
    const response = await axios.get(`${API_BASE}/tickets/my-purchases`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Ranking
  getRanking: async (raffleId) => {
    const response = await axios.get(`${API_BASE}/ranking/${raffleId}`);
    return response.data;
  },

  // Claims
  submitClaim: async (claimData) => {
    const response = await axios.post(`${API_BASE}/claims/submit`, claimData, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  verifyClaimCode: async (claimId, code) => {
    const response = await axios.post(
      `${API_BASE}/claims/${claimId}/verify`,
      { code },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  uploadClaimDocument: async (claimId) => {
    const response = await axios.post(
      `${API_BASE}/claims/${claimId}/upload-document`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  uploadClaimVideo: async (claimId) => {
    const response = await axios.post(
      `${API_BASE}/claims/${claimId}/upload-video`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  getMyClaims: async () => {
    const response = await axios.get(`${API_BASE}/claims/my-claims`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Referrals
  getMyReferralInfo: async () => {
    const response = await axios.get(`${API_BASE}/referrals/my-info`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  getReferralRanking: async () => {
    const response = await axios.get(`${API_BASE}/referrals/ranking`);
    return response.data;
  },

  getMyRewards: async () => {
    const response = await axios.get(`${API_BASE}/referrals/my-rewards`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  claimReward: async (rewardId) => {
    const response = await axios.post(
      `${API_BASE}/referrals/claim-reward/${rewardId}`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  // Social Proof
  getSocialProofPrizes: async () => {
    const response = await axios.get(`${API_BASE}/social-proof/prizes`);
    return response.data;
  },

  validateReferralCode: async (code) => {
    const response = await axios.get(`${API_BASE}/referrals/validate/${code}`);
    return response.data;
  },

  // Payment
  getPaymentInfo: async (purchaseId) => {
    const response = await axios.get(`${API_BASE}/payment/info/${purchaseId}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  submitPaymentProof: async (purchaseId, proofDescription) => {
    const response = await axios.post(
      `${API_BASE}/payment/submit-proof`,
      { purchase_id: purchaseId, proof_description: proofDescription },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  // QR Payment System
  createQRPurchase: async (raffleId, packageTickets, paymentMethod) => {
    const response = await axios.post(
      `${API_BASE}/payment/qr/create`,
      { raffle_id: raffleId, package_tickets: packageTickets, payment_method: paymentMethod },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  confirmQRPayment: async (purchaseId) => {
    const response = await axios.post(
      `${API_BASE}/payment/qr/confirm/${purchaseId}`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  checkPaymentStatus: async (purchaseId) => {
    const response = await axios.get(
      `${API_BASE}/payment/qr/status/${purchaseId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  // Password Reset
  forgotPassword: async (email) => {
    const response = await axios.post(`${API_BASE}/auth/forgot-password`, { email });
    return response.data;
  },

  verifyResetToken: async (token) => {
    const response = await axios.get(`${API_BASE}/auth/verify-reset-token?token=${token}`);
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await axios.post(`${API_BASE}/auth/reset-password`, { 
      token, 
      new_password: newPassword 
    });
    return response.data;
  },

  // Admin
  admin: {
    getDashboard: async () => {
      const response = await axios.get(`${API_BASE}/admin/dashboard`, {
        headers: getAuthHeaders()
      });
      return response.data;
    },

    getRaffles: async () => {
      const response = await axios.get(`${API_BASE}/admin/raffles`, {
        headers: getAuthHeaders()
      });
      return response.data;
    },

    createRaffle: async (raffleData) => {
      const response = await axios.post(`${API_BASE}/admin/raffles`, raffleData, {
        headers: getAuthHeaders()
      });
      return response.data;
    },

    executeDraw: async (raffleId) => {
      const response = await axios.post(
        `${API_BASE}/admin/raffles/${raffleId}/execute-draw`,
        {},
        { headers: getAuthHeaders() }
      );
      return response.data;
    },

    getRngLogs: async (raffleId) => {
      const response = await axios.get(`${API_BASE}/admin/rng-logs/${raffleId}`, {
        headers: getAuthHeaders()
      });
      return response.data;
    },

    getClaims: async () => {
      const response = await axios.get(`${API_BASE}/admin/claims`, {
        headers: getAuthHeaders()
      });
      return response.data;
    },

    approveClaim: async (claimId) => {
      const response = await axios.post(
        `${API_BASE}/admin/claims/${claimId}/approve`,
        {},
        { headers: getAuthHeaders() }
      );
      return response.data;
    },

    rejectClaim: async (claimId) => {
      const response = await axios.post(
        `${API_BASE}/admin/claims/${claimId}/reject`,
        {},
        { headers: getAuthHeaders() }
      );
      return response.data;
    },

    getPendingPayments: async () => {
      const response = await axios.get(`${API_BASE}/admin/pending-payments`, {
        headers: getAuthHeaders()
      });
      return response.data;
    },

    approvePayment: async (purchaseId) => {
      const response = await axios.post(
        `${API_BASE}/admin/approve-payment/${purchaseId}`,
        {},
        { headers: getAuthHeaders() }
      );
      return response.data;
    },

    rejectPayment: async (purchaseId) => {
      const response = await axios.post(
        `${API_BASE}/admin/reject-payment/${purchaseId}`,
        {},
        { headers: getAuthHeaders() }
      );
      return response.data;
    },

    getReferralRanking: async () => {
      const response = await axios.get(`${API_BASE}/admin/referral-ranking`, {
        headers: getAuthHeaders()
      });
      return response.data;
    },

    processMonthlyReferralPrize: async () => {
      const response = await axios.post(
        `${API_BASE}/admin/process-monthly-referral-prize`,
        {},
        { headers: getAuthHeaders() }
      );
      return response.data;
    },

    getMonthlyPrizesHistory: async () => {
      const response = await axios.get(`${API_BASE}/admin/monthly-prizes-history`, {
        headers: getAuthHeaders()
      });
      return response.data;
    },

    markMonthlyPrizePaid: async (prizeId) => {
      const response = await axios.post(
        `${API_BASE}/admin/mark-monthly-prize-paid/${prizeId}`,
        {},
        { headers: getAuthHeaders() }
      );
      return response.data;
    },

    // QR Payment Admin
    getPendingQRPayments: async () => {
      const response = await axios.get(`${API_BASE}/admin/payment/qr/pending`, {
        headers: getAuthHeaders()
      });
      return response.data;
    },

    approveQRPayment: async (purchaseId) => {
      const response = await axios.post(
        `${API_BASE}/admin/payment/qr/approve/${purchaseId}`,
        {},
        { headers: getAuthHeaders() }
      );
      return response.data;
    },

    rejectQRPayment: async (purchaseId) => {
      const response = await axios.post(
        `${API_BASE}/admin/reject-payment/${purchaseId}`,
        {},
        { headers: getAuthHeaders() }
      );
      return response.data;
    },

    clearAllPayments: async () => {
      const response = await axios.delete(
        `${API_BASE}/admin/payments/clear-all`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    },

    resetAllData: async () => {
      const response = await axios.delete(
        `${API_BASE}/admin/reset-all-data`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    },

    // Tickets Management
    getAllTickets: async () => {
      const response = await axios.get(`${API_BASE}/admin/tickets`, {
        headers: getAuthHeaders()
      });
      return response.data;
    },

    updateTicketStatus: async (ticketId, status) => {
      const response = await axios.patch(
        `${API_BASE}/admin/tickets/${ticketId}/status`,
        { status },
        { headers: getAuthHeaders() }
      );
      return response.data;
    },

    // Social Proof Settings
    getSocialProofSettings: async () => {
      const response = await axios.get(`${API_BASE}/admin/social-proof/settings`, {
        headers: getAuthHeaders()
      });
      return response.data;
    },

    updateSocialProofSettings: async (settings) => {
      const response = await axios.patch(
        `${API_BASE}/admin/social-proof/settings`,
        settings,
        { headers: getAuthHeaders() }
      );
      return response.data;
    },

    // Prize Management
    markPrizeAsPaid: async (raffleId, prizeType, userId, city = 'Colombia') => {
      const response = await axios.post(
        `${API_BASE}/admin/prizes/mark-paid`,
        null,
        { 
          params: { raffle_id: raffleId, prize_type: prizeType, user_id: userId, city },
          headers: getAuthHeaders() 
        }
      );
      return response.data;
    },

    getPaidPrizes: async () => {
      const response = await axios.get(`${API_BASE}/admin/prizes/list`, {
        headers: getAuthHeaders()
      });
      return response.data;
    },
  },

  // Platform Stats
  getPlatformStats: async () => {
    const response = await axios.get(`${API_BASE}/platform-stats`);
    return response.data;
  },

  updatePlatformStats: async (stats) => {
    const response = await axios.patch(`${API_BASE}/admin/platform-stats`, stats, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Admin Participants
  getAdminParticipants: async (status = 'all', participationLevel = 'all') => {
    const response = await axios.get(
      `${API_BASE}/admin/participants?status=${status}&participation_level=${participationLevel}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  exportParticipantsCSV: async () => {
    const response = await axios.get(`${API_BASE}/admin/participants/export`, {
      headers: getAuthHeaders()
    });
    return response.data;
  },

  // Setup (for development)
  setup: {
    createAdmin: async () => {
      const response = await axios.post(`${API_BASE}/setup/create-admin`);
      return response.data;
    },

    createSampleRaffle: async () => {
      const response = await axios.post(`${API_BASE}/setup/create-sample-raffle`);
      return response.data;
    },
  },
};
